import { all, call, fork, put, select, take } from 'redux-saga/effects';

import { request } from '!core/api';
import {
  dbConfigurationsSlice,
  userInfoSlice,
  userInstrumentsServices,
  userInstrumentsSlice,
} from '!core/redux';
import { isOtpActive } from '!core/redux/dbConfigurations';
import { disclaimersServices } from '»components/molecules/TrlDisclaimer';
import { login as cryptoLogin } from '»utils/crypto';
import { tokenLanguageCode, tokenLanguageId } from '»utils/jwt';
import { navigate } from '»utils/navigation';

import { menusSlice, onboardingSlice } from '../..';
import {
  legalStatusListSelector,
  onboardingCreateAccountDisclaimerSelector,
} from '../Onboarding/redux/onboarding.selectors';
import { authSlice } from '../redux';
import { loginSlice } from './_login.slice';
import { loginServices } from './login.services';

const loginSuccess = function* (user, data) {
  try {
    // eslint-disable-next-line no-undef
    const myConfig = config();
    const { showTermsAndConditions } = myConfig;

    const { token } = data[0];
    const languageCode = tokenLanguageCode(token);

    const isLoginOtpActive = yield select(isOtpActive, 'ENFORCE_OTP_LOGIN');
    const legalStatusList = yield select(legalStatusListSelector);
    const acceptUsePersonalDataLabel = yield select(
      onboardingCreateAccountDisclaimerSelector,
    );

    request.setAuth(user, token);
    request.setCulture({
      languageId: tokenLanguageId(token),
      languageCode,
    });

    const localStorageUserLanguageCode =
      localStorage.getItem('userLanguageCode');

    if (localStorageUserLanguageCode !== languageCode) {
      if (acceptUsePersonalDataLabel !== undefined) {
        const disclaimerResponse = yield call(
          disclaimersServices.getDisclaimerNoAuth,
          { context: 'ONBOARDING_ACCEPT' },
        );
        yield put(
          onboardingSlice.actions.setCreateAccountDisclaimer({
            disclaimer: disclaimerResponse[0]?.body,
          }),
        );
      }

      if (legalStatusList.length !== 0)
        yield put(onboardingSlice.actions.getLegalStatusListRequest());
    }

    localStorage.setItem('userLanguageCode', languageCode);

    const auth = {
      token,
      user,
      isAuthenticated: !isLoginOtpActive,
      loginId: data[0]['login-id'],
    };

    yield put(loginSlice.actions.loginSuccess(auth));

    if (!showTermsAndConditions && !isLoginOtpActive) {
      navigate('/Dashboard');
      yield put(
        menusSlice.actions.setActiveMenuItem({ activeMenuItem: 'DASHBOARD' }),
      );
    }
  } catch {
    yield put(loginSlice.actions.loginFailure());
  }
};

const authenticate = function* ({ user, password }) {
  try {
    const data1 = yield call(loginServices.salt, user);
    const { salt } = data1[0];
    const { hashPassword } = cryptoLogin(password, salt);

    const isLoginOtpActive = yield select(isOtpActive, 'ENFORCE_OTP_LOGIN');

    const data = yield call(loginServices.login, user, hashPassword);

    yield call(loginSuccess, user, data);

    if (isLoginOtpActive) {
      yield put(
        loginSlice.actions.setLoginCurrentStep({ direction: 'forward' }),
      );
    } else {
      const userInstrumentsData = yield call(
        userInstrumentsServices.userInstruments,
      );

      yield all([
        put(menusSlice.actions.menuRequest()),
        put(userInfoSlice.actions.userInfoRequest()),
        put(
          userInstrumentsSlice.actions.userInstrumentsSucess(
            userInstrumentsData,
          ),
        ),
        put(
          dbConfigurationsSlice.actions.dbConfigurationsRequest({
            detailIds: [
              'PRIM_EXPIRE_TIME',
              'CONTACT_TYPE_TMOVEL',
              'CONTACT_TYPE_EMAIL',
              'CLIENT_SUPPORT_PHONE_NUMBER',
              'HAS_FUNDS',
            ],
            parameterId: 'CR_CHNN_UI',
            noAuth: false,
          }),
        ),
      ]);

      // eslint-disable-next-line no-undef
      const myConfig = config();
      const { showTermsAndConditions } = myConfig;

      if (showTermsAndConditions)
        yield put(
          authSlice.actions.getUserProcessRequest({ processId: 'COND_GERAIS' }),
        );
    }
  } catch {
    yield put(loginSlice.actions.loginFailure());
  }
};

const authenticateFlowSaga = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const data = yield take(loginSlice.actions.loginRequest.type);
        const { user, password } = data.payload;
        yield call(authenticate, { user, password });
      }
    } catch {
      yield put(loginSlice.actions.loginFailure());
    }
  }
};

export const loginFlowSaga = function* () {
  try {
    yield fork(authenticateFlowSaga);
  } catch {}
};
